/**
 * Blog Predictive Search - Live search with suggestions
 * Uses Shopify Predictive Search API
 */
(function() {
  'use strict';

  const DEBOUNCE_MS = 300;
  const MIN_QUERY_LENGTH = 2;

  function getSuggestUrl() {
    if (typeof Theme !== 'undefined' && Theme.routes && Theme.routes.predictive_search_url) {
      return Theme.routes.predictive_search_url.replace(/\/suggest\/?$/, '') + '/suggest.json';
    }
    if (typeof Shopify !== 'undefined' && Shopify.routes && Shopify.routes.root) {
      return Shopify.routes.root + 'search/suggest.json';
    }
    return '/search/suggest.json';
  }

  function debounce(fn, delay) {
    let timeoutId;
    return function(...args) {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  function escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  function highlightMatch(text, query) {
    if (!query || !text) return escapeHtml(text || '');
    const escaped = escapeHtml(text);
    const regex = new RegExp(`(${escapeRegex(query)})`, 'gi');
    return escaped.replace(regex, '<mark>$1</mark>');
  }

  function escapeRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
    } catch {
      return '';
    }
  }

  function buildResultsHtml(data, query) {
    const results = data.resources?.results || {};
    const queries = results.queries || [];
    const products = results.products || [];
    const articles = results.articles || [];
    const pages = results.pages || [];
    const collections = results.collections || [];

    const hasResults = queries.length > 0 || products.length > 0 || articles.length > 0 || pages.length > 0 || collections.length > 0;

    if (!hasResults) {
      return `
        <div class="blog-search-results__empty">
          <p>No results found for "${escapeHtml(query)}"</p>
          <a href="/search?q=${encodeURIComponent(query)}&type=article" class="blog-search-results__view-all">View all results</a>
        </div>
      `;
    }

    let html = '<div class="blog-search-results__inner">';

    if (queries.length > 0) {
      html += '<div class="blog-search-results__group"><div class="blog-search-results__title">Suggestions</div><ul class="blog-search-results__list">';
      queries.forEach(q => {
        html += `<li><a href="${escapeHtml(q.url)}" class="blog-search-results__link">${q.styled_text || highlightMatch(q.text, query)}</a></li>`;
      });
      html += '</ul></div>';
    }

    if (articles.length > 0) {
      html += '<div class="blog-search-results__group"><div class="blog-search-results__title">Articles</div><ul class="blog-search-results__list">';
      articles.forEach(article => {
        const date = formatDate(article.published_at);
        html += `
          <li>
            <a href="${escapeHtml(article.url)}" class="blog-search-results__link blog-search-results__link--article">
              <span class="blog-search-results__link-title">${highlightMatch(article.title, query)}</span>
              ${date ? `<span class="blog-search-results__link-meta">${date}</span>` : ''}
            </a>
          </li>
        `;
      });
      html += '</ul></div>';
    }

    if (products.length > 0) {
      html += '<div class="blog-search-results__group"><div class="blog-search-results__title">Products</div><ul class="blog-search-results__list">';
      products.forEach(product => {
        const img = product.featured_image?.url || product.image ? `<img src="${escapeHtml((product.featured_image || product).url)}" alt="" width="40" height="40" loading="lazy">` : '';
        const price = product.price ? `<span class="blog-search-results__price">${formatMoney(product.price)}</span>` : '';
        html += `
          <li>
            <a href="${escapeHtml(product.url)}" class="blog-search-results__link blog-search-results__link--product">
              ${img ? `<span class="blog-search-results__thumb">${img}</span>` : ''}
              <span class="blog-search-results__link-content">
                <span class="blog-search-results__link-title">${highlightMatch(product.title, query)}</span>
                ${price}
              </span>
            </a>
          </li>
        `;
      });
      html += '</ul></div>';
    }

    if (pages.length > 0) {
      html += '<div class="blog-search-results__group"><div class="blog-search-results__title">Pages</div><ul class="blog-search-results__list">';
      pages.forEach(page => {
        html += `
          <li>
            <a href="${escapeHtml(page.url)}" class="blog-search-results__link">
              <span class="blog-search-results__link-title">${highlightMatch(page.title, query)}</span>
            </a>
          </li>
        `;
      });
      html += '</ul></div>';
    }

    if (collections.length > 0) {
      html += '<div class="blog-search-results__group"><div class="blog-search-results__title">Collections</div><ul class="blog-search-results__list">';
      collections.forEach(coll => {
        html += `
          <li>
            <a href="${escapeHtml(coll.url)}" class="blog-search-results__link">
              <span class="blog-search-results__link-title">${highlightMatch(coll.title, query)}</span>
            </a>
          </li>
        `;
      });
      html += '</ul></div>';
    }

    html += `
      <div class="blog-search-results__footer">
        <a href="/search?q=${encodeURIComponent(query)}&type=article" class="blog-search-results__view-all">View all results for "${escapeHtml(query)}"</a>
      </div>
    </div>`;

    return html;
  }

  function formatMoney(value) {
    if (value == null) return '';
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney) {
      return Shopify.formatMoney(value);
    }
    var num = Number(value);
    if (num > 1000) {
      return '$' + (num / 100).toFixed(2);
    }
    return '$' + num.toFixed(2);
  }

  function initBlogSearch() {
    const wrapper = document.querySelector('[data-blog-search]');
    if (!wrapper) return;

    const input = wrapper.querySelector('[data-blog-search-input]');
    const resultsEl = wrapper.querySelector('[data-blog-search-results]');
    const form = wrapper.querySelector('[data-blog-search-form]');

    if (!input || !resultsEl) return;

    let abortController = null;

    const fetchSuggestions = debounce(async (query) => {
      if (!query || query.length < MIN_QUERY_LENGTH) {
        resultsEl.innerHTML = '';
        resultsEl.classList.remove('is-visible');
        input.setAttribute('aria-expanded', 'false');
        return;
      }

      if (abortController) abortController.abort();
      abortController = new AbortController();

      resultsEl.innerHTML = '<div class="blog-search-results__loading">Searching...</div>';
      resultsEl.classList.add('is-visible');
      input.setAttribute('aria-expanded', 'true');

      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'query,product,article,page,collection',
        'resources[limit]': '5',
        'resources[limit_scope]': 'each'
      });

      try {
        const url = getSuggestUrl() + '?' + params.toString();
        const response = await fetch(url, { signal: abortController.signal });
        const data = await response.json();

        if (input.value.trim().length < MIN_QUERY_LENGTH) {
          resultsEl.innerHTML = '';
          resultsEl.classList.remove('is-visible');
          return;
        }

        resultsEl.innerHTML = buildResultsHtml(data, query.trim());
      } catch (err) {
        if (err.name === 'AbortError') return;
        resultsEl.innerHTML = '<div class="blog-search-results__error">Unable to load suggestions. Try searching.</div>';
      }
    }, DEBOUNCE_MS);

    input.addEventListener('input', () => fetchSuggestions(input.value.trim()));
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= MIN_QUERY_LENGTH && resultsEl.innerHTML) {
        resultsEl.classList.add('is-visible');
        input.setAttribute('aria-expanded', 'true');
      }
    });

    document.addEventListener('click', (e) => {
      if (!wrapper.contains(e.target)) {
        resultsEl.classList.remove('is-visible');
        input.setAttribute('aria-expanded', 'false');
      }
    });

    input.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        resultsEl.classList.remove('is-visible');
        input.setAttribute('aria-expanded', 'false');
        input.blur();
      }
    });

    form.addEventListener('submit', () => {
      resultsEl.classList.remove('is-visible');
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initBlogSearch);
  } else {
    initBlogSearch();
  }
})();
